/**
 * 工具市场页面 - 浏览和订阅已发布的工具
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LayoutWithSidebar } from '@/components/LayoutWithSidebar';
import { toolsAPIV2, type Tool } from '@/services/toolsApiV2';
import { subscriptionsAPI, type Subscription } from '@/services/subscriptionsApi';
import { useAuthStore } from '@/stores/auth';
import { Search, Loader2, Package, Users, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

export function ToolsMarketPage() {
  const { accessToken, isAuthenticated, setAuth } = useAuthStore();
  const [tools, setTools] = useState<Tool[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const [batchSubscribing, setBatchSubscribing] = useState<string | null>(null);
  const [expandedServers, setExpandedServers] = useState<Set<string>>(new Set());

  // 验证 Web Session Cookie（用于 App WebView 单点登录）
  useEffect(() => {
    const verifyWebSession = async () => {
      // 如果已经通过 JWT 登录，跳过 Web Session 验证
      if (isAuthenticated && accessToken) {
        console.log('✅ 已通过 JWT 登录，跳过 Web Session 验证');
        return;
      }

      // 检查是否有 web_session cookie
      const cookies = document.cookie;
      console.log('🍪 当前 Cookies:', cookies);
      
      if (!cookies.includes('web_session=')) {
        console.log('⚠️  未找到 web_session cookie');
        return;
      }

      console.log('🔍 发现 web_session cookie，开始验证...');

      try {
        const response = await fetch('/api/auth/verify-web-session', {
          method: 'GET',
          credentials: 'include', // 重要：包含 Cookie
        });

        const data = await response.json();
        console.log('📥 Web Session 验证响应:', data);

        if (data.success && data.data?.user && data.data?.accessToken) {
          console.log('✅ Web Session 验证成功:', data.data.user);
          console.log('🔑 收到 Access Token:', data.data.accessToken.substring(0, 30) + '...');
          
          // 使用返回的 accessToken 和 refreshToken 登录
          // setAuth 签名: (user, accessToken, refreshToken)
          setAuth(
            data.data.user,
            data.data.accessToken,
            data.data.refreshToken || ''
          );
          
          toast.success(`欢迎回来，${data.data.user.username}！`);
        } else {
          console.log('❌ Web Session 验证失败:', data.message);
        }
      } catch (error) {
        console.error('❌ Web Session 验证错误:', error);
      }
    };

    verifyWebSession();
  }, []); // 只在组件挂载时执行一次

  useEffect(() => {
    loadData();
  }, [isAuthenticated, accessToken]);

  // 当工具加载完成后，默认展开所有服务器分组
  useEffect(() => {
    if (tools.length > 0) {
      const serverNames = new Set(tools.map(tool => tool.server_name || '未知服务器'));
      setExpandedServers(serverNames);
    }
  }, [tools]);

  const loadData = async () => {
    setLoading(true);
    try {
      // 加载工具列表
      const toolsRes = await toolsAPIV2.getPublishedTools(
        { page: 1, page_size: 100 },
        accessToken || undefined
      );

      if (toolsRes.success && toolsRes.data) {
        setTools(toolsRes.data.data);
      }

      // 如果已登录，加载订阅列表
      if (isAuthenticated && accessToken) {
        try {
          const subsRes = await subscriptionsAPI.getMySubscriptions(accessToken);
          if (subsRes.success && subsRes.data) {
            // 后端返回的格式是 { data: { data: [...], pagination: {...} } }
            const subscriptionsData = (subsRes.data as any).data || subsRes.data;
            // 确保是数组
            setSubscriptions(Array.isArray(subscriptionsData) ? subscriptionsData : []);
          } else {
            setSubscriptions([]);
          }
        } catch (subError) {
          console.error('加载订阅失败:', subError);
          setSubscriptions([]);
        }
      } else {
        // 未登录时设置为空数组
        setSubscriptions([]);
      }
    } catch (error: any) {
      console.error('加载数据失败:', error);
      toast.error('加载数据失败: ' + error.message);
      // 确保即使出错也设置为空数组
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (toolId: string) => {
    if (!isAuthenticated || !accessToken) {
      toast.error('请先登录');
      return;
    }

    setSubscribing(toolId);
    try {
      const response = await subscriptionsAPI.subscribe(toolId, accessToken);
      if (response.success) {
        toast.success('订阅成功');
        // 🆕 只更新订阅列表，不刷新整页
        const subsRes = await subscriptionsAPI.getMySubscriptions(accessToken);
        if (subsRes.success && subsRes.data) {
          const subscriptionsData = (subsRes.data as any).data || subsRes.data;
          setSubscriptions(Array.isArray(subscriptionsData) ? subscriptionsData : []);
        }
        // 🆕 更新工具的订阅数
        setTools(prevTools => prevTools.map(tool => 
          tool.id === toolId 
            ? { ...tool, subscription_count: (tool.subscription_count || 0) + 1 }
            : tool
        ));
      }
    } catch (error: any) {
      toast.error('订阅失败: ' + error.message);
    } finally {
      setSubscribing(null);
    }
  };

  const handleUnsubscribe = async (toolId: string) => {
    if (!accessToken) return;

    setSubscribing(toolId);
    try {
      const response = await subscriptionsAPI.unsubscribe(toolId, accessToken);
      if (response.success) {
        toast.success('已取消订阅');
        // 🆕 只更新订阅列表，不刷新整页
        const subsRes = await subscriptionsAPI.getMySubscriptions(accessToken);
        if (subsRes.success && subsRes.data) {
          const subscriptionsData = (subsRes.data as any).data || subsRes.data;
          setSubscriptions(Array.isArray(subscriptionsData) ? subscriptionsData : []);
        }
        // 🆕 更新工具的订阅数
        setTools(prevTools => prevTools.map(tool => 
          tool.id === toolId 
            ? { ...tool, subscription_count: Math.max((tool.subscription_count || 0) - 1, 0) }
            : tool
        ));
      }
    } catch (error: any) {
      toast.error('取消订阅失败: ' + error.message);
    } finally {
      setSubscribing(null);
    }
  };

  const handleBatchSubscribe = async (serverName: string) => {
    if (!isAuthenticated || !accessToken) {
      toast.error('请先登录');
      return;
    }

    setBatchSubscribing(serverName);
    try {
      const response = await subscriptionsAPI.batchSubscribeByServer(serverName, accessToken);
      if (response.success) {
        toast.success(response.message || `批量订阅成功`);
        // 重新加载订阅列表
        await loadData();
      }
    } catch (error: any) {
      toast.error('批量订阅失败: ' + error.message);
    } finally {
      setBatchSubscribing(null);
    }
  };

  const toggleServerExpanded = (serverName: string) => {
    setExpandedServers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(serverName)) {
        newSet.delete(serverName);
      } else {
        newSet.add(serverName);
      }
      return newSet;
    });
  };

  const isSubscribed = (toolId: string) => {
    // 确保 subscriptions 是数组
    if (!Array.isArray(subscriptions)) {
      return false;
    }
    // 处理嵌套的数据结构 { subscription, tool, server }
    return subscriptions.some(item => {
      const sub = (item as any).subscription || item;
      return sub.tool_id === toolId;
    });
  };

  const filteredTools = tools.filter(tool => {
    const keyword = searchKeyword.toLowerCase();
    return (
      tool.name?.toLowerCase().includes(keyword) ||
      tool.display_name?.toLowerCase().includes(keyword) ||
      tool.description?.toLowerCase().includes(keyword) ||
      tool.category?.toLowerCase().includes(keyword) ||
      tool.server_name?.toLowerCase().includes(keyword)
    );
  });

  // 按服务器名称分组工具
  const groupedTools = filteredTools.reduce((acc, tool) => {
    const serverName = tool.server_name || '未知服务器';
    if (!acc[serverName]) {
      acc[serverName] = [];
    }
    acc[serverName].push(tool);
    return acc;
  }, {} as Record<string, Tool[]>);

  // 获取服务器订阅状态
  const getServerSubscriptionStatus = (_serverName: string, serverTools: Tool[]) => {
    const subscribedCount = serverTools.filter(tool => isSubscribed(tool.id)).length;
    return {
      subscribedCount,
      totalCount: serverTools.length,
      allSubscribed: subscribedCount === serverTools.length,
      noneSubscribed: subscribedCount === 0,
    };
  };

  if (loading) {
    return (
      <LayoutWithSidebar title="工具市场">
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </LayoutWithSidebar>
    );
  }

  return (
    <LayoutWithSidebar title="工具市场">
      <div className="container mx-auto py-4 px-3 sm:py-8 sm:px-4 max-w-7xl">
        {/* 标题区域 - 移动端优化 */}
        <div className="mb-4 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">工具市场</h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            浏览并订阅可用的MCP工具
          </p>
        </div>

      {!isAuthenticated && (
        <Alert className="mb-4 sm:mb-6">
          <AlertDescription className="text-sm">
            请从左侧菜单登录以订阅和使用工具
          </AlertDescription>
        </Alert>
      )}

      {/* 搜索栏 - 移动端优化 */}
      <div className="mb-4 sm:mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="搜索工具..."
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            className="pl-10 h-10 sm:h-auto text-base"
          />
        </div>
      </div>

      {/* 按服务器分组显示工具 */}
      <div className="space-y-6">
        {Object.entries(groupedTools).map(([serverName, serverTools]) => {
          const isExpanded = expandedServers.has(serverName);
          const subscriptionStatus = getServerSubscriptionStatus(serverName, serverTools);
          const isBatchLoading = batchSubscribing === serverName;

          return (
            <div key={serverName} className="border rounded-lg overflow-hidden">
              {/* 服务器分组头部 */}
              <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3">
                {/* 第一行：服务器名称和展开/折叠按钮 */}
                <div className="flex items-center gap-2 mb-2">
                  <button
                    onClick={() => toggleServerExpanded(serverName)}
                    className="flex items-center gap-2 hover:text-blue-600 transition-colors flex-1 min-w-0"
                  >
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-5 w-5 flex-shrink-0" />
                    )}
                    <Package className="h-5 w-5 text-blue-500 flex-shrink-0" />
                    <span className="font-semibold text-lg truncate">{serverName}</span>
                  </button>
                </div>
                
                {/* 第二行：工具数量和订阅状态 */}
                <div className="flex items-center gap-2 mb-2 ml-9">
                  <Badge variant="outline" className="text-xs">
                    {serverTools.length} 个工具
                  </Badge>
                  {subscriptionStatus.subscribedCount > 0 && (
                    <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs">
                      已订阅 {subscriptionStatus.subscribedCount}/{subscriptionStatus.totalCount}
                    </Badge>
                  )}
                </div>
                
                {/* 第三行：批量订阅按钮 */}
                {isAuthenticated && !subscriptionStatus.allSubscribed && (
                  <div className="ml-9">
                    <Button
                      size="sm"
                      onClick={() => handleBatchSubscribe(serverName)}
                      disabled={isBatchLoading}
                      className="w-full sm:w-auto"
                    >
                      {isBatchLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {subscriptionStatus.noneSubscribed ? '批量订阅' : '订阅剩余'}
                    </Button>
                  </div>
                )}
              </div>

              {/* 工具列表 */}
              {isExpanded && (
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {serverTools.map((tool) => {
                    const subscribed = isSubscribed(tool.id);
                    const isLoading = subscribing === tool.id;

                    return (
                      <Card key={tool.id} className="flex flex-col hover:shadow-lg transition-shadow">
                        <CardHeader className="pb-3 sm:pb-6">
                          <div className="flex items-start justify-between gap-2">
                            <Package className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500 mb-2 flex-shrink-0" />
                            {subscribed && (
                              <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                已订阅
                              </Badge>
                            )}
                          </div>
                          <CardTitle className="text-lg sm:text-xl break-words">{tool.display_name || tool.name}</CardTitle>
                          <CardDescription className="line-clamp-3 text-sm">
                            {tool.description || '暂无描述'}
                          </CardDescription>
                        </CardHeader>

                        <CardContent className="flex-1 py-3 sm:py-4">
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center text-gray-600 dark:text-gray-400">
                              <Users className="h-4 w-4 mr-2 flex-shrink-0" />
                              <span className="text-xs sm:text-sm">{tool.subscription_count || 0} 人订阅</span>
                            </div>
                          </div>
                        </CardContent>

                        <CardFooter className="pt-3 sm:pt-4">
                          {isAuthenticated ? (
                            subscribed ? (
                              <Button
                                variant="outline"
                                className="w-full h-10 sm:h-auto text-sm sm:text-base"
                                onClick={() => handleUnsubscribe(tool.id)}
                                disabled={isLoading}
                              >
                                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                取消订阅
                              </Button>
                            ) : (
                              <Button
                                className="w-full h-10 sm:h-auto text-sm sm:text-base"
                                onClick={() => handleSubscribe(tool.id)}
                                disabled={isLoading}
                              >
                                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                订阅
                              </Button>
                            )
                          ) : (
                            <Button variant="outline" className="w-full h-10 sm:h-auto text-sm sm:text-base" disabled>
                              登录后订阅
                            </Button>
                          )}
                        </CardFooter>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {Object.keys(groupedTools).length === 0 && (
        <div className="text-center py-12">
          <Package className="h-12 w-12 sm:h-16 sm:w-16 mx-auto text-gray-400 mb-4" />
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            {searchKeyword ? '未找到匹配的工具' : '暂无可用工具'}
          </p>
        </div>
      )}
    </div>
    </LayoutWithSidebar>
  );
}

